import { useEffect } from 'react';
import { Capacitor, type PluginListenerHandle } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import { useAuth } from '../hooks/useAuth';
import { useResolvedTheme } from '../themes/themeMode';
import { syncNativeChrome } from '../lib/nativeChrome';

/**
 * Native-shell lifecycle glue (Capacitor iPad app), shared pattern with the
 * Project Portal's FieldOfflineRuntime. Browser behavior is unchanged —
 * every native listener below is gated on Capacitor.isNativePlatform().
 *
 * Today: keeps the status-bar band color matched to the active surface
 * (login vs shell, light vs dark) via the local HTSChrome plugin.
 *
 * Forward: the appStateChange hooks are where the offline upload queue
 * drains — foreground is the reliable "we might have signal again" moment
 * (WKWebViews don't always fire window 'online'), and backgrounding grants
 * iOS's ~30s grace window; the OS-side BGTaskScheduler (AppDelegate.swift,
 * `com.hts.sitesurvey.uploadsync`) is the opportunistic later retry that
 * relays the same wakeup.
 */
export function NativeRuntime() {
  const { isAuthenticated } = useAuth();
  const resolvedTheme = useResolvedTheme();

  // Status-bar chrome follows theme + auth surface.
  useEffect(() => {
    syncNativeChrome(resolvedTheme, isAuthenticated);
  }, [resolvedTheme, isAuthenticated]);

  // Upload-queue drain seam (offline photos/plans — next step). Registered
  // only inside the native shell; online/offline still fire in browsers and
  // drive the same (currently empty) drain.
  useEffect(() => {
    const drain = (trigger: string) => {
      // TODO(offline-queue): replay queued photo/floorplan uploads here.
      if (import.meta.env.DEV) {
        console.debug(`[native-runtime] drain trigger: ${trigger} (queue not built yet)`);
      }
    };

    const online = () => drain('online');
    window.addEventListener('online', online);

    let appListener: PluginListenerHandle | null = null;
    if (Capacitor.isNativePlatform()) {
      void CapApp.addListener('appStateChange', ({ isActive }) => {
        // Foreground = reliable reconnect moment; background = iOS's ~30s
        // grace window after the tech locks the iPad mid-upload.
        drain(isActive ? 'foreground' : 'background');
      }).then((l) => { appListener = l; }).catch(() => undefined);
    }

    return () => {
      window.removeEventListener('online', online);
      appListener?.remove().catch(() => undefined);
    };
  }, []);

  return null;
}
